import { useRef, useState } from 'react';

import { AsyncCommand } from '../../../shared/common';
import ActionButton from '../../../shared/components/ActionButton';
import { TertiaryButtonLink } from '../../../shared/components/Button/LinkButtons';
import SecondaryButton from '../../../shared/components/Button/SecondaryButton';
import TertiaryButton from '../../../shared/components/Button/TertiaryButton';
import Image from '../../../shared/components/Image/Image';
import { Tooltip, TooltipTrigger } from '../../../shared/components/Tooltip/Tooltip';
import ExternalConfiguratorModalState from '../ExternalConfiguratorModal/ExternalConfiguratorModalState';
import ProductModelInfo from '../ProductModelInfo/ProductModelInfo';
import css from './ModularProductDetails.css';
import ModularProductDetailsState from './ModularProductDetailsState';

interface ModularProductDetailsProps {
  askForPriceCommand: AsyncCommand;
  canRequestQuote: boolean;
  state: ModularProductDetailsState;
  externalConfigurator?: ExternalConfiguratorModalState;
}

const ModularProductDetails = ({
  askForPriceCommand,
  canRequestQuote,
  externalConfigurator,
  state,
}: ModularProductDetailsProps) => {
  const canConfigureInExternalConfigurator = !!externalConfigurator?.canConfigure();

  return (
    <div className={css.root}>
      {state.image.url && (
        <Image
          className={css.image}
          classNameImageElement={css.imageElement}
          classNameFallbackElement={css.imageElement}
          src={state.image.url}
          alt={state.image.name}
          width={545}
          height={650}
          placeholder={null}
        />
      )}
      <div className={css.textContentContainer}>
        <ProductModelInfo name={state.name} description={state.description} icons={state.icons} />
        <div className={css.actionButtonsContainer}>
          {canRequestQuote && (
            <ActionButton className={css.actionButton} command={askForPriceCommand} buttonElement={SecondaryButton}>
              {state.translation.actions.askForPriceButton}
            </ActionButton>
          )}
          {canConfigureInExternalConfigurator && (
            <ExternalConfiguratorButton
              label={state.translation.actions.configureChairButton}
              state={externalConfigurator}
            />
          )}
          {state.configuratorUrl && (
            <TertiaryButtonLink className={css.actionButton} href={state.configuratorUrl.path}>
              {state.translation.actions.configureChairButton}
            </TertiaryButtonLink>
          )}
        </div>
        {canRequestQuote && canConfigureInExternalConfigurator && (
          <p className={css.hint}>{state.translation.actions.externalModularProductHint}</p>
        )}
      </div>
    </div>
  );
};

export default ModularProductDetails;

interface ExternalConfiguratorButtonProps {
  label: string;
  state: ExternalConfiguratorModalState;
}

/**
 * The shared Button can't take the ref and props a TooltipTrigger passes to its trigger,
 * so the wrapper opens the tooltip itself through a controlled TooltipTrigger.
 * TODO: Let TooltipTrigger handle it once Button is built on React Aria.
 */
const ExternalConfiguratorButton = ({ label, state }: ExternalConfiguratorButtonProps) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  return (
    <TooltipTrigger isOpen={isTooltipOpen}>
      <div
        ref={triggerRef}
        className={css.actionButton}
        onPointerEnter={() => setIsTooltipOpen(true)}
        onPointerLeave={() => setIsTooltipOpen(false)}
        onPointerDown={() => setIsTooltipOpen(false)}
        onFocus={() => setIsTooltipOpen(true)}
        onBlur={() => setIsTooltipOpen(false)}
        onKeyDown={(e) => e.key === 'Escape' && setIsTooltipOpen(false)}
      >
        <TertiaryButton type="button" className={css.tooltipTriggerButton} onClick={() => state.open()}>
          {label}
        </TertiaryButton>
      </div>
      <Tooltip triggerRef={triggerRef} offset={12} placement="top">
        {state.translation.triggerTooltip}
      </Tooltip>
    </TooltipTrigger>
  );
};
